import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { useTranslation } from "react-i18next";
import {
  createDesignFolder,
  getDesignFolders,
} from "../../api/AI/presentacionesApi";
import { getTemporadas } from "../../api/Temporadas/temporadaApi";
import { getCustomers } from "../../api/Customer/customerApi";
import { optionsFromCustomers } from "../Meetings/meetingMoodboardClientOptions";
import { sortTemporadasFromLabel } from "../Meetings/meetingTemporadaSort";
import {
  bob,
  cloudLift,
  motionSafe,
  rise,
  studioDialogBackdropSx,
  studioDialogPaperSx,
} from "./studioDialogMotion";

function DriveUploadStage({ accent }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 88,
        mt: 1.25,
        mb: 0.5,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: alpha(accent, 0.08),
        border: "1px solid",
        borderColor: alpha(accent, 0.22),
        animation: `${rise} 320ms ease both`,
        ...motionSafe,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: "18%",
          top: 18,
          width: 48,
          height: 56,
          borderRadius: 1.25,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.35),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 6px 14px ${alpha(accent, 0.14)}`,
          animation: `${bob} 2.2s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <ImageOutlinedIcon sx={{ fontSize: 26, color: accent }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: "16%",
          top: 16,
          width: 54,
          height: 54,
          borderRadius: 1.5,
          bgcolor: alpha(accent, 0.14),
          border: "1.5px solid",
          borderColor: alpha(accent, 0.45),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 8px 18px ${alpha(accent, 0.2)}`,
          animation: `${cloudLift} 2.4s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <CloudUploadOutlinedIcon sx={{ fontSize: 28, color: accent }} />
      </Box>
    </Box>
  );
}

export default function SaveDesignToDriveDialog({
  open,
  onClose,
  onSave,
  saving = false,
  error = "",
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accent = "#1473e6";

  const [temporadas, setTemporadas] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [temporada, setTemporada] = useState("");
  const [customer, setCustomer] = useState(null);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [temps, customersData] = await Promise.all([
          getTemporadas(),
          getCustomers(),
        ]);
        if (cancelled) return;
        setTemporadas(
          sortTemporadasFromLabel(Array.isArray(temps) ? temps : [], "AW2025"),
        );
        setCustomerOptions(
          optionsFromCustomers(
            Array.isArray(customersData) ? customersData : [],
          ),
        );
      } catch (e) {
        if (!cancelled) {
          setLocalError(
            e?.message || t("presentacionesSearch.common.unknownError"),
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, t]);

  const loadFolders = useCallback(async () => {
    const season = (temporada || "").trim();
    const customerName = customer?.label || "";
    if (!season || !customerName) {
      setFolders([]);
      return;
    }
    setLoadingFolders(true);
    setLocalError("");
    try {
      const data = await getDesignFolders({
        temporada: season,
        customerName,
      });
      const names = data.folders || [];
      setFolders(names);
      setSelectedFolder((current) => (names.includes(current) ? current : ""));
    } catch (e) {
      setFolders([]);
      setLocalError(
        e?.response?.data?.error ||
          t("presentacionesSearch.newDesign.loadFoldersError"),
      );
    } finally {
      setLoadingFolders(false);
    }
  }, [customer, temporada, t]);

  useEffect(() => {
    if (!open) return;
    loadFolders();
  }, [open, loadFolders]);

  const handleCreateFolder = async () => {
    const name = (newFolderName || "").trim();
    const season = (temporada || "").trim();
    const customerName = customer?.label || "";
    if (!name || !season || !customerName) return;
    setCreatingFolder(true);
    setLocalError("");
    try {
      const data = await createDesignFolder({
        folder: name,
        temporada: season,
        customerName,
      });
      const folder = data.folder || name;
      setFolders((prev) =>
        prev.includes(folder) ? prev : [...prev, folder].sort(),
      );
      setSelectedFolder(folder);
      setNewFolderName("");
    } catch (e) {
      setLocalError(
        e?.response?.data?.error ||
          e?.message ||
          t("presentacionesSearch.common.unknownError"),
      );
    } finally {
      setCreatingFolder(false);
    }
  };

  const ready =
    Boolean((temporada || "").trim()) &&
    Boolean(customer?.label) &&
    Boolean((selectedFolder || "").trim());

  const pathPreview = [
    "Agentes-STAGE",
    "temporadas",
    (temporada || "").trim(),
    customer?.label,
    "AIDisenos",
    (selectedFolder || "").trim(),
  ]
    .filter(Boolean)
    .join(" / ");

  const handleConfirm = () => {
    if (!ready || saving) return;
    onSave?.({
      temporada: temporada.trim(),
      customerName: customer.label,
      folder: selectedFolder.trim(),
    });
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: isDark ? alpha("#fff", 0.04) : alpha("#000", 0.02),
    },
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      keepMounted={false}
      transitionDuration={160}
      slotProps={{
        backdrop: { sx: studioDialogBackdropSx },
      }}
      BackdropProps={{ sx: studioDialogBackdropSx }}
      PaperProps={{ sx: studioDialogPaperSx }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.25,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: `linear-gradient(135deg, ${alpha(accent, 0.12)} 0%, ${alpha(
            accent,
            0.02,
          )} 55%)`,
          animation: `${rise} 280ms ease both`,
          ...motionSafe,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
          {t("presentacionesSearch.newDesign.saveToDriveTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t("presentacionesSearch.newDesign.saveToDriveLead")}
        </Typography>
        <DriveUploadStage accent={accent} />
      </Box>

      <DialogContent sx={{ pt: 2 }}>
        {(error || localError) && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error || localError}
          </Alert>
        )}

        <FormControl
          size="small"
          fullWidth
          required
          sx={{ mb: 1.5, ...fieldSx }}
        >
          <InputLabel id="drive-season-label">
            {t("presentacionesSearch.newDesign.seasonLabel")}
          </InputLabel>
          <Select
            labelId="drive-season-label"
            value={temporada}
            label={t("presentacionesSearch.newDesign.seasonLabel")}
            onChange={(e) => setTemporada(e.target.value)}
          >
            {temporadas.map((temp) => {
              const label = String(temp.TEMPORADA || "").trim();
              if (!label) return null;
              return (
                <MenuItem key={temp.ID_TEMPORADA || label} value={label}>
                  {label}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <Autocomplete
          options={customerOptions}
          value={customer}
          onChange={(_, value) => setCustomer(value)}
          getOptionLabel={(opt) => opt?.label || ""}
          isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              required
              label={t("presentacionesSearch.newDesign.clientLabel")}
              sx={fieldSx}
            />
          )}
          sx={{ mb: 1.5 }}
        />

        <FormControl
          size="small"
          fullWidth
          required
          sx={{ mb: 1.25, ...fieldSx }}
        >
          <InputLabel id="drive-folder-label">
            {t("presentacionesSearch.newDesign.folderLabel")}
          </InputLabel>
          <Select
            labelId="drive-folder-label"
            value={selectedFolder}
            label={t("presentacionesSearch.newDesign.folderLabel")}
            onChange={(e) => setSelectedFolder(e.target.value)}
            disabled={loadingFolders || !temporada.trim() || !customer?.label}
            onOpen={() => {
              if (!folders.length) loadFolders();
            }}
            displayEmpty
            renderValue={(v) => v}
          >
            <MenuItem value="">
              <em>{t("presentacionesSearch.newDesign.folderChoose")}</em>
            </MenuItem>
            {!loadingFolders && folders.length === 0 && (
              <MenuItem value="" disabled>
                <em>{t("presentacionesSearch.newDesign.noFolders")}</em>
              </MenuItem>
            )}
            {folders.map((f) => (
              <MenuItem key={f} value={f}>
                {f}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
          <TextField
            size="small"
            fullWidth
            placeholder={t("presentacionesSearch.newDesign.newFolderName")}
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleCreateFolder();
              }
            }}
            sx={fieldSx}
          />
          <Button
            variant="outlined"
            onClick={handleCreateFolder}
            disabled={
              creatingFolder ||
              !newFolderName.trim() ||
              !temporada.trim() ||
              !customer?.label
            }
            sx={{ whiteSpace: "nowrap", minWidth: 120 }}
          >
            {creatingFolder
              ? t("presentacionesSearch.newDesign.creatingFolder")
              : t("presentacionesSearch.newDesign.createFolder")}
          </Button>
        </Box>

        {pathPreview ? (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "primary.main",
              fontWeight: 600,
              wordBreak: "break-word",
            }}
          >
            {t("presentacionesSearch.newDesign.saveIn")}: {pathPreview}
          </Typography>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} disabled={saving} color="inherit">
          {t("presentacionesSearch.newDesign.saveToDriveCancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!ready || saving}
          startIcon={
            saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <CloudUploadOutlinedIcon />
            )
          }
          sx={{
            bgcolor: accent,
            fontWeight: 700,
            px: 2.25,
            "&:hover": { bgcolor: "#0d66d0" },
          }}
        >
          {saving
            ? t("presentacionesSearch.newDesign.saveToDriveSaving")
            : t("presentacionesSearch.newDesign.saveToDriveConfirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
